import React, {
  Children,
  type HTMLAttributes,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { Icon } from "@purpur/icon";
import { chevronLeft } from "@purpur/icon/assets/chevron-left";
import { chevronRight } from "@purpur/icon/assets/chevron-right";
import { List, Root } from "@radix-ui/react-tabs";
import c from "classnames/bind";

import { isTabContent, TabContent } from "./tab-content";
import { TabHeader } from "./tab-header";
import styles from "./tabs.module.scss";
import {
  createTabChangeDetailEvent,
  type TabChangeDetail,
  type TabsCmp,
  type TabsVariant,
} from "./tabs.utils";

type TabsProps = {
  children: Array<ReactElement<typeof TabContent>> | ReactElement<typeof TabContent>;
  variant?: TabsVariant;
  negative?: boolean;
  fullWidth?: boolean;
  /**
   * The controlled value of the tabs. Must be used in conjunction with onChange.
   */
  value?: string;
  /**
   * Event handler called when the value changes.
   * */
  onChange?: (event: CustomEvent<TabChangeDetail>) => void;
  defaultValue?: string;
  /**
   * When true, animates the height of the component when switching tabs.
   * */
  animateHeight?: boolean;
  "data-testid"?: string;
} & Pick<HTMLAttributes<HTMLDivElement>, "style" | "className">;

const cx = c.bind(styles);
const rootClassName = "purpur-tabs";

const scrollToTarget = (target: HTMLElement, tabList?: HTMLDivElement | null) => {
  /**
   * scroll may not be available in test runner, so check existence before
   * proceeding. We can't use scrollIntoView() because Safari doesn't support
   * it fully.
   */
  if (
    typeof target?.getBoundingClientRect !== "function" ||
    typeof tabList?.scroll !== "function"
  ) {
    return;
  }

  const targetRect = target.getBoundingClientRect();
  const wrapperRect = tabList.getBoundingClientRect();
  const wrapperWidth = tabList.clientWidth;
  const wrapperOffset = parseInt(getComputedStyle(tabList).borderLeftWidth?.split("px")[0], 10);
  const offset = wrapperRect.left + (isNaN(wrapperOffset) ? 0 : wrapperOffset);
  let left;

  // Handle forward navigation
  if (targetRect.right > wrapperRect.right) {
    left = targetRect.left + tabList.scrollLeft;
    left = left + targetRect.width - wrapperWidth + wrapperWidth * 0.1;
    left = left - offset;
  }

  // Handle backwards navigation
  if (targetRect.left < wrapperRect.left) {
    left = targetRect.left + tabList.scrollLeft;
    left = left - wrapperWidth * 0.1;
    left = left - offset;
  }

  if (left !== undefined) {
    tabList.scroll({ left, behavior: "smooth" });
  }
};

export const Tabs: TabsCmp<TabsProps> = ({
  children,
  variant = "line",
  negative = false,
  fullWidth = false,
  onChange,
  className,
  defaultValue,
  animateHeight = false,
  "data-testid": dataTestId,
  value,
  ...props
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(-1);
  const [scrollClasses, setScrollClasses] = useState<{ [key: string]: boolean }>({});
  const [selectedTriggerOffset, setSelectedTriggerOffset] = useState(0);
  const [selectedTriggerWidth, setSelectedTriggerWidth] = useState(0);
  const tabContentChildren = Children.toArray(children).filter(isTabContent);
  const tabList = useRef<HTMLDivElement>(null);
  const tabChildren = useRef<HTMLButtonElement[]>(new Array(tabContentChildren.length));
  const sideScrollAdjustmentSize = 200;
  const isLineVariant = variant === "line";

  const classNames = cx(
    rootClassName,
    `${rootClassName}--${variant}${negative ? "-negative" : ""}`,
    { [`${rootClassName}--full-width`]: fullWidth },
    className
  );

  const tabIds = Children.map(tabContentChildren, ({ props: { tabId } }) => tabId);

  const getDefaultTabIndex = () => {
    const defaultIndex = tabIds.findIndex((tabIndex) => tabIndex === defaultValue);
    return defaultIndex >= 0 ? defaultIndex : 0;
  };

  const [activeIndex, setActiveIndex] = useState(getDefaultTabIndex);

  // Internal active tab ID — keeps Radix always controlled so our keydown handler
  // can activate tabs directly without relying on Radix's mouse/focus events.
  const [activeTabId, setActiveTabId] = useState<string>(
    value ?? defaultValue ?? tabContentChildren[0].props.tabId
  );

  useEffect(() => {
    if (value) {
      setActiveTabId(value);
      setActiveIndex(tabContentChildren.findIndex((child) => child.props.tabId === value));
    }
  }, [tabContentChildren, value]);

  if (new Set(tabIds).size !== tabIds.length) {
    throw new Error("tabId must be unique");
  }

  const getTestId = (name: string, id?: string) =>
    id || dataTestId ? `${id || dataTestId}-${name}` : undefined;

  const handleLinePosition = () => {
    if (!isLineVariant) {
      return;
    }

    const activeTabElement = tabChildren.current[activeIndex];

    setSelectedTriggerOffset(activeTabElement?.offsetLeft || 0);
    setSelectedTriggerWidth(activeTabElement?.getBoundingClientRect().width || 0);
  };

  const handleTabChange = (_value: string) => {
    setActiveTabId(_value);
    if (isLineVariant && !value) {
      setActiveIndex(tabContentChildren.findIndex((child) => child.props.tabId === _value));
    }
    onChange?.(createTabChangeDetailEvent(_value));
  };

  const handleScrollButtonClick = (side: "left" | "right") => {
    if (tabList?.current) {
      const { scrollLeft } = tabList.current;
      const modifier = side === "left" ? -sideScrollAdjustmentSize : sideScrollAdjustmentSize;

      tabList.current.scroll({ left: scrollLeft + modifier, behavior: "smooth" });
    }
  };

  const ScrollButton = ({ side }: { side: "left" | "right" }) => (
    <button
      className={cx(`${rootClassName}__scroll-button`, `${rootClassName}__scroll-button--${side}`)}
      onClick={() => handleScrollButtonClick(side)}
      type="button"
      aria-hidden="true"
      tabIndex={-1}
      data-testid={getTestId("scroll-button")}
    >
      <Icon svg={side === "left" ? chevronLeft : chevronRight} size="md" />
    </button>
  );

  useEffect(() => {
    const setNewHeight = () => {
      if (contentRef.current && animateHeight) {
        const newHeight = contentRef.current.offsetHeight;
        setHeight(newHeight);
      }
    };

    const handleResize = () => {
      setNewHeight();
      handleLinePosition();
    };

    setNewHeight();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [activeIndex, animateHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleLinePosition();
  }, [activeIndex, fullWidth, tabContentChildren.length, variant]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const root = tabList.current;
    if (!root) return;

    const updateScrollState = () => {
      const { scrollLeft, scrollWidth, clientWidth } = root;

      const canScroll = scrollWidth > clientWidth;
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      if (!canScroll) {
        setScrollClasses({});
        return;
      }

      setScrollClasses({
        [`${rootClassName}__wrapper--scroll-start`]: !atStart,
        [`${rootClassName}__wrapper--scroll-end`]: !atEnd,
      });
    };

    updateScrollState();

    root.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      root.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  const handleTabListKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    const currentIndex = parseInt(target.dataset.index ?? "", 10);
    if (isNaN(currentIndex)) return;

    const total = tabContentChildren.length;
    let nextIndex: number | null = null;

    switch (e.key) {
      case "ArrowRight":
        nextIndex = (currentIndex + 1) % total;
        break;
      case "ArrowLeft":
        nextIndex = (currentIndex - 1 + total) % total;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = total - 1;
        break;
    }

    if (nextIndex !== null) {
      const nextTabId = tabContentChildren[nextIndex].props.tabId;
      handleTabChange(nextTabId);
      tabChildren.current[nextIndex]?.focus();
    }
  };

  return (
    <Root
      defaultValue={defaultValue ?? tabContentChildren[0].props.tabId}
      onValueChange={handleTabChange}
      data-testid={dataTestId}
      className={classNames}
      value={value ?? activeTabId}
      activationMode="manual"
      {...props}
    >
      <div className={cx(`${rootClassName}__container`)}>
        <div className={cx(`${rootClassName}__wrapper`, scrollClasses)}>
          <List
            ref={(el) => {
              tabList.current = el;
            }}
            className={cx(`${rootClassName}__list`)}
            onKeyDown={handleTabListKeyDown}
          >
            {Children.map(tabContentChildren, (child, index) => {
              const { name, tabId, "data-testid": childDataTestId } = child.props;
              return (
                <TabHeader
                  data-testid={getTestId("header", childDataTestId)}
                  index={index}
                  tabId={tabId}
                  ref={(el) => {
                    if (el) {
                      tabChildren.current[index] = el;
                    }
                  }}
                  onFocus={(e) => {
                    scrollToTarget(e.target as HTMLElement, tabList.current);
                  }}
                  variant={variant}
                  negative={negative}
                  fullWidth={fullWidth}
                >
                  {name}
                </TabHeader>
              );
            })}
            {isLineVariant && (
              <div
                className={cx(`${rootClassName}__selected-border`)}
                style={{
                  width: selectedTriggerWidth,
                  transform: `translateX(${selectedTriggerOffset}px)`,
                }}
                data-testid={getTestId("selected-border")}
              />
            )}
          </List>
          <ScrollButton side="left" />
          <ScrollButton side="right" />
        </div>
        <div
          className={animateHeight ? cx(`${rootClassName}__content-wrapper`) : ""}
          style={{ height: animateHeight && height > -1 ? `${height}px` : "auto" }}
        >
          <div ref={contentRef} className={cx(`${rootClassName}__content-container`)}>
            {Children.map(tabContentChildren, (child) => child)}
          </div>
        </div>
      </div>
    </Root>
  );
};

Tabs.Content = TabContent;

export * from "./tabs.utils";
